import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Megaphone, Target, BarChart3, Send } from "lucide-react";

export const AdvertisersPage = () => {
  const [form, setForm] = useState({
    title: "", description: "", link_url: "", image_url: "",
    advertiser_name: "", advertiser_email: "", placement: "banner",
  });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/ads", form);
      toast.success("Merci ! Votre demande a été envoyée.");
      setForm({ title: "", description: "", link_url: "", image_url: "", advertiser_name: "", advertiser_email: "", placement: "banner" });
    } catch (err) {
      toast.error("Erreur lors de l'envoi");
    } finally { setSending(false); }
  };

  return (
    <div className="px-6 md:px-12 py-10 max-w-6xl mx-auto pb-32" data-testid="advertisers-page">
      <p className="inline-flex items-center gap-2 tag-pill mb-5 bg-brand-orange/10 border-brand-orange/30 text-brand-orange">
        <Megaphone size={12} /> Espace annonceurs
      </p>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-4 text-balance">
        Atteignez une audience<br /><span className="bg-gradient-to-r from-brand-red to-brand-yellow bg-clip-text text-transparent">passionnée et engagée.</span>
      </h1>
      <p className="text-white/70 max-w-2xl text-lg mb-14">
        Voamada rassemble des fans de musique malgache et africaine, des labels, des organisateurs de concerts. Faites passer votre message.
      </p>

      <div className="grid md:grid-cols-3 gap-6 mb-14 stagger">
        <Perk icon={<Target size={22} />} title="Ciblage précis" desc="Par pays, genre musical, langue, type d'utilisateur (fans, artistes, labels)." />
        <Perk icon={<BarChart3 size={22} />} title="Performance mesurée" desc="Impressions, clics, CTR en temps réel sur votre dashboard annonceur." />
        <Perk icon={<Megaphone size={22} />} title="Formats multiples" desc="Bannière d'accueil, sidebar, annonce inline entre les tracks." />
      </div>

      <div className="border border-white/10 bg-white/[0.02] rounded-2xl p-8">
        <h2 className="font-display text-2xl font-black mb-6">Soumettre une campagne</h2>
        <form onSubmit={submit} className="grid md:grid-cols-2 gap-4">
          <Field label="Nom de l'annonceur *"><Input value={form.advertiser_name} onChange={(v) => setForm({ ...form, advertiser_name: v })} testid="ad-name" required /></Field>
          <Field label="Email de contact *"><Input type="email" value={form.advertiser_email} onChange={(v) => setForm({ ...form, advertiser_email: v })} testid="ad-email" required /></Field>
          <Field label="Titre de l'annonce *"><Input value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="ad-title" required /></Field>
          <Field label="URL de destination *"><Input value={form.link_url} onChange={(v) => setForm({ ...form, link_url: v })} placeholder="https://..." testid="ad-link" required /></Field>
          <Field label="URL de l'image (optionnel)" full><Input value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} placeholder="https://..." testid="ad-image" /></Field>
          <Field label="Description *" full>
            <textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-brand-red/50 min-h-[100px]" data-testid="ad-description-input" />
          </Field>
          <Field label="Placement" full>
            <select value={form.placement} onChange={(e) => setForm({ ...form, placement: e.target.value })} className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 outline-none" data-testid="ad-placement-select">
              <option value="banner" className="bg-brand-dark">Bannière d'accueil</option>
              <option value="sidebar" className="bg-brand-dark">Sidebar</option>
              <option value="inline" className="bg-brand-dark">Inline entre tracks</option>
            </select>
          </Field>

          <div className="md:col-span-2 text-sm text-white/60 bg-white/[0.03] border border-white/10 rounded-xl p-4">
            Votre demande sera examinée par notre équipe sous 48h. Nous vous contacterons pour finaliser le paiement et activer votre campagne.
          </div>
          <button type="submit" disabled={sending} className="btn-primary md:col-span-2 inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="ad-submit-btn">
            <Send size={16} /> {sending ? "Envoi..." : "Envoyer ma demande"}
          </button>
        </form>
      </div>
    </div>
  );
};

const Perk = ({ icon, title, desc }) => (
  <div className="border border-white/10 bg-white/[0.02] rounded-2xl p-6">
    <div className="w-11 h-11 rounded-full bg-brand-orange/15 text-brand-orange flex items-center justify-center mb-4">{icon}</div>
    <h4 className="font-display text-lg font-bold mb-2">{title}</h4>
    <p className="text-white/60 text-sm">{desc}</p>
  </div>
);
const Field = ({ label, children, full }) => (<div className={full ? "md:col-span-2" : ""}><label className="block text-[10px] tracking-[0.25em] uppercase text-white/60 font-bold mb-2">{label}</label>{children}</div>);
const Input = ({ type = "text", value, onChange, placeholder, testid, required }) => (<input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required={required} className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-brand-red/50" data-testid={`${testid}-input`} />);

export default AdvertisersPage;
